import type { ReactElement, ReactNode } from "react";
import { useRouteMatch } from "react-router-dom";
import { AppBar } from "@/react/components/Layout/AppBar";
import { BrowserNotificationPrompt } from "@/react/components/Layout/BrowserNotificationPrompt";
import { MobileBottomToolbar } from "@/react/components/Layout/MobileBottomToolbar";
import { Body } from "@/react/components/Layout/StandardLayout/Body";
import { Breadcrumb } from "@/react/components/Layout/StandardLayout/Breadcrumb";
import { Content } from "@/react/components/Layout/StandardLayout/Content";
import { Header } from "@/react/components/Layout/StandardLayout/Header";
import { Left } from "@/react/components/Layout/StandardLayout/Left";
import { Right } from "@/react/components/Layout/StandardLayout/Right";
import { SideBar } from "@/react/components/Spaces/Sidebar";
import { isEventsSpace } from "@/react/helpers/spaceHelpers";
import { useAppendBodyClasses } from "@/react/hooks/useAppendBodyClasses";
import { useLgScreenMediaQuery } from "@/react/hooks/useMediaQuery";
import { usePunditUserContext, useSpacesContext } from "@circle-react/contexts";
import { useLayoutPartialsContext } from "@circle-react/contexts/layoutPartialsContext";
import { classNames } from "@circle-react/helpers/twMergeWithCN";
import type { Space } from "@circle-react/types";
import { MobileDrawer } from "../MobileDrawer";
import "./styles.scss";

interface ShouldDisableRightSidebarProps {
  disableRightSidebar?: boolean;
  postSlug?: string;
  space?: Space;
}

const shouldDisableRightSidebar = ({
  postSlug,
  space,
  disableRightSidebar,
}: ShouldDisableRightSidebarProps) => {
  if (disableRightSidebar) return true;

  return postSlug && postSlug !== "members" && isEventsSpace(space)
    ? false
    : space?.hide_right_sidebar;
};

export interface StandardLayoutProps {
  bodyClassNames?: string;
  children: ReactNode;
  disableRightSidebar?: boolean;
  disableSidebar?: boolean;
  hideAppBar?: boolean;
  renderLeftSidebar?: (() => ReactElement) | null;
}

interface RouteParams {
  postSlug?: string;
  spaceSlug?: string;
}

export const StandardLayout = ({
  disableSidebar = false,
  disableRightSidebar = false,
  children,
  bodyClassNames = "",
  renderLeftSidebar = null,
  hideAppBar = false,
}: StandardLayoutProps) => {
  const { currentCommunityMember } = usePunditUserContext();
  const containerProps: Record<string, unknown> = {};
  const { data: { mobile_drawer_html } = {} } = useLayoutPartialsContext();
  if (!disableSidebar && mobile_drawer_html) {
    containerProps["data-controller"] = "mobile-sidebar";
  }
  useAppendBodyClasses(
    classNames("is-standard-layout react-standard-layout", bodyClassNames),
  );
  const isLgScreen = useLgScreenMediaQuery();
  const postRouteMatch = useRouteMatch<RouteParams>("/c/:spaceSlug/:postSlug");
  const spaceRouteMatch = useRouteMatch<RouteParams>("/c/:spaceSlug");

  const spaceSlug = spaceRouteMatch?.params.spaceSlug || "";
  const postSlug = postRouteMatch?.params.postSlug || "";

  const isHomePage = useRouteMatch("/home")?.isExact;
  const isFeedPage = useRouteMatch("/feed")?.isExact;
  const isSpaceShowPage = useRouteMatch("/c/:spaceSlug")?.isExact;

  const space = useSpacesContext().helpers.findBy({ slug: spaceSlug });

  // TODO: refactor so that we rely on child props to set this value.
  const isRightSidebarDisabled = shouldDisableRightSidebar({
    disableRightSidebar,
    postSlug,
    space,
  });

  const shouldShowMobileBottomToolbar = Boolean(
    !!currentCommunityMember &&
      !isLgScreen &&
      !!(isHomePage || isFeedPage || isSpaceShowPage),
  );

  return (
    <div className="community__wrapper bg-secondary" {...containerProps}>
      <div id="mobile-dropdown-container" />
      {/*TODO Add support for mobile menu and also add condition based on screensize*/}
      {!disableSidebar && (
        <MobileDrawer renderLeftSidebar={renderLeftSidebar} />
      )}
      {/*TODO: REACTIFY revisit to take full advantage of react/router/context */}
      <div
        id="panel"
        className={classNames("community__content trix-v2", {
          "community__content--no-sidebar": disableSidebar,
        })}
        data-target="mobile-sidebar.panel"
      >
        {!disableSidebar && !hideAppBar && <AppBar />}
        <div
          className={classNames("standard-layout", {
            "has-sidebar": !disableSidebar,
            "no-right-sidebar": isRightSidebarDisabled,
          })}
        >
          {/*TODO: Pass it from outside*/}
          {!disableSidebar && (
            <Left>{renderLeftSidebar ? renderLeftSidebar() : <SideBar />}</Left>
          )}
          {children}
          <BrowserNotificationPrompt />
          {shouldShowMobileBottomToolbar && <MobileBottomToolbar />}
        </div>
      </div>
    </div>
  );
};
StandardLayout.Header = Header;
StandardLayout.Breadcrumb = Breadcrumb;
StandardLayout.Content = Content;
StandardLayout.Body = Body;
StandardLayout.Left = Left;
StandardLayout.Right = Right;
